import React, { FC, useMemo, useState } from 'react';
import { Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import { BcfImportApi } from 'Data/BcfImport.api';
import { complexfilterActivities, filterActivities } from 'Utils/ActivitiesUtils';
import { enumToStringValueMap } from 'Data/interfaces/Activities/IFilters';
import { IDragAndDropDataFormat, ITopicDnDResponse } from 'Data/interfaces/Activities/IDragAndDropDataFormat';
import { FiltersRecordType } from 'Data/interfaces/Activities/IFilters';
import Modal from 'Components/UI/Modal';
import Button from 'Components/UI/Button';
import { Icon } from 'Components/UI';
import {
  Body,
  Content,
  FooterButtons,
  FooterDivider,
  InfoBox,
  InfoIcon,
  InfoText,
  RadioGroup,
  RadioLabel,
  RadioOption,
  Section,
  SectionDescription,
  SectionLabel,
  StatCard,
  StatLabel,
  StatsRow,
  StatValue,
  SuccessBox,
  SuccessDescription,
  SuccessTitle,
} from './styles';

interface IBcfExportModalProps {
  visible: boolean;
  constructionSiteId: string | number;
  listActivities: IDragAndDropDataFormat | undefined;
  filters: FiltersRecordType;
  hasFilters: boolean;
  updatedDateIds?: string[];
  searchQuery: string;
  onClose: () => void;
}

const BcfExportModal: FC<IBcfExportModalProps> = ({
  visible,
  constructionSiteId,
  listActivities,
  filters,
  hasFilters,
  updatedDateIds,
  searchQuery,
  onClose,
}) => {
  const { t } = useTranslation('activities');
  const [exportMode, setExportMode] = useState<'all' | 'filtered'>('all');
  const [bcfVersion, setBcfVersion] = useState<'3.0' | '2.1'>('3.0');
  const [isExporting, setIsExporting] = useState(false);
  const [exportInitiated, setExportInitiated] = useState(false);

  const { filteredActivities, totalActivities } = useMemo(() => {
    if (!listActivities?.activities) {
      return { filteredActivities: [], totalActivities: 0 };
    }

    const allActivities = Object.values(listActivities.activities);

    if (!hasFilters && !searchQuery) {
      return { filteredActivities: allActivities, totalActivities: allActivities.length };
    }

    let filtered = allActivities;

    if (hasFilters && Object.keys(filters).length > 0) {
      filtered = complexfilterActivities<ITopicDnDResponse, typeof enumToStringValueMap>({
        filters,
        array: allActivities,
        enumToStringValueMap,
        updatedDateIds,
      });
    }

    if (searchQuery) {
      filtered = filterActivities(filtered, searchQuery);
    }

    return { filteredActivities: filtered, totalActivities: allActivities.length };
  }, [listActivities, filters, hasFilters, searchQuery, updatedDateIds]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const topicGuids = exportMode === 'filtered'
        ? filteredActivities.map(a => a.Guid)
        : undefined;

      await BcfImportApi.exportBcf({
        constructionSiteId,
        version: bcfVersion,
        topicGuids,
      });

      setExportInitiated(true);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    setExportMode('all');
    setBcfVersion('3.0');
    setExportInitiated(false);
    onClose();
  };

  const exportCount = exportMode === 'filtered' ? filteredActivities.length : totalActivities;

  const footerButtons = exportInitiated ? (
    <FooterButtons>
      <Button type="primary" onClick={handleClose}>
        {t('bcfExport.close')}
      </Button>
    </FooterButtons>
  ) : (
    <FooterButtons>
      <Button type="text" onClick={handleClose} disabled={isExporting}>
        {t('bcfExport.cancel')}
      </Button>
      <Button
        type="primary"
        onClick={handleExport}
        loading={isExporting}
        disabled={exportCount === 0}
      >
        {t('bcfExport.export')}
      </Button>
    </FooterButtons>
  );

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      width={520}
      title={t('bcfExport.title')}
      newHeader
      footerButtons={footerButtons}
      footerActive={false}
      maskClosable={false}
    >
      {exportInitiated ? (
        <Body>
          <SuccessBox>
            <Icon icon="confirmar" customSize={32} color="verde" />
            <SuccessTitle>{t('bcfExport.exportStartedTitle')}</SuccessTitle>
            <SuccessDescription>{t('bcfExport.exportStartedDescription')}</SuccessDescription>
          </SuccessBox>
        </Body>
      ) : (
        <Body>
          <Content>
            <Section>
              <SectionLabel>{t('bcfExport.scopeTitle')}</SectionLabel>
              <SectionDescription>{t('bcfExport.scopeDescription')}</SectionDescription>
              <RadioGroup>
                <RadioOption
                  selected={exportMode === 'all'}
                  onClick={() => setExportMode('all')}
                >
                  <Radio checked={exportMode === 'all'} />
                  <RadioLabel>{`${t('bcfExport.allIssues')} (${totalActivities})`}</RadioLabel>
                </RadioOption>
                <RadioOption
                  selected={exportMode === 'filtered'}
                  onClick={() => setExportMode('filtered')}
                  disabled={!hasFilters && !searchQuery}
                >
                  <Radio checked={exportMode === 'filtered'} />
                  <RadioLabel>{`${t('bcfExport.filteredIssues')} (${filteredActivities.length})`}</RadioLabel>
                </RadioOption>
              </RadioGroup>
              {(hasFilters || searchQuery) && (
                <StatsRow>
                  <StatCard>
                    <StatValue>{totalActivities}</StatValue>
                    <StatLabel>{t('bcfExport.total')}</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{filteredActivities.length}</StatValue>
                    <StatLabel>{t('bcfExport.filtered')}</StatLabel>
                  </StatCard>
                </StatsRow>
              )}
            </Section>

            <Section>
              <SectionLabel>{t('bcfExport.versionTitle')}</SectionLabel>
              <SectionDescription>{t('bcfExport.versionDescription')}</SectionDescription>
              <RadioGroup>
                <RadioOption
                  selected={bcfVersion === '3.0'}
                  onClick={() => setBcfVersion('3.0')}
                >
                  <Radio checked={bcfVersion === '3.0'} />
                  <RadioLabel>{t('bcfExport.version30')}</RadioLabel>
                </RadioOption>
                <RadioOption
                  selected={bcfVersion === '2.1'}
                  onClick={() => setBcfVersion('2.1')}
                >
                  <Radio checked={bcfVersion === '2.1'} />
                  <RadioLabel>{t('bcfExport.version21')}</RadioLabel>
                </RadioOption>
              </RadioGroup>
            </Section>

            <InfoBox>
              <InfoIcon>&#8505;&#65039;</InfoIcon>
              <InfoText>{t('bcfExport.infoText')}</InfoText>
            </InfoBox>
          </Content>
        </Body>
      )}
      <FooterDivider />
    </Modal>
  );
};

export default BcfExportModal;
